import { plannedMinutesForJob, plannedMinutesForBooking } from './capacityPlanning'
import { normaliseJobStatus } from './utils'
import { addDays, dayAt, inRange, periodPreset } from './reportingView.mjs'
export { verifyCapacitySnapshot } from './capacityContract.mjs'

const ACTIVE = new Set(['awaiting_customer_item', 'received', 'estimate_sent', 'awaiting_parts', 'ready_for_collection'])
const HISTORICAL = new Set(['completed', 'closed', 'collected'])
const CANCELLED = new Set(['cancelled', 'voided'])
export const WAITING = { awaiting_parts: 'Awaiting parts', awaiting_customer_item: 'Awaiting customer item', estimate_sent: 'Awaiting quote approval' }
const text = value => String(value ?? '').trim()
const unique = values => [...new Set(values)]
const positive = value => value !== null && value !== '' && Number.isFinite(Number(value)) && Number(value) > 0
export function validDay(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    && Number.isFinite(Date.parse(`${value}T12:00:00Z`)) && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value
}
export function capacityPreset(key, today = dayAt()) {
  const week = periodPreset(key === 'next' ? 'next' : 'week', today)
  return key === 'four' ? { from: week.from, to: addDays(week.from, 27) } : week
}
export function capacityMinutes(value) {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) || Number(value) < 0) return null
  const minutes = Math.round(Number(value) * 60)
  return Number.isSafeInteger(minutes) ? minutes : null
}
const monday = day => periodPreset('week', day).from

function planning(row, services, booking) {
  const ids = unique((row.service_ids || []).filter(Boolean))
  const matched = services.filter(s => ids.includes(s.id))
  const current = ids.length > 0 && matched.length === ids.length
  const source = current ? matched : Array.isArray(row.service_snapshot) ? row.service_snapshot : []
  // Live captures always supply the canonical v100 result. The helper fallback
  // supports local fixtures; the boundary validator rejects missing live fields.
  const minutes = Object.prototype.hasOwnProperty.call(row, 'planned_minutes') ? row.planned_minutes
    : booking ? plannedMinutesForBooking(row, { services }) : plannedMinutesForJob(row, { services })
  const complete = source.length > 0 && source.every(s => positive(s.expected_labour_minutes))
  const modules = unique(source.map(s => s.module_key).filter(Boolean))
  let module = booking
    ? current && modules.length === 1 && source.every(s => s.module_key === modules[0]) ? modules[0] : null
    : row.module_key || 'suspension'
  if (!booking && source.some(s => s.module_key && s.module_key !== module)) module = null
  return { minutes, complete, source: current ? 'Current service book' : 'Stored service snapshot',
    module: ['general', 'suspension'].includes(module) ? module : 'unknown' }
}

export function capacityRows(snapshot, today = dayAt()) {
  const { jobs, bookings, services, team } = snapshot
  const jobById = new Map(jobs.map(j => [j.id, j]))
  const reverse = new Map(bookings.map(b => [b.id, jobs.filter(j => j.booking_request_id === b.id)]))
  const ambiguousJobs = new Set()
  for (const booking of bookings) {
    const linked = reverse.get(booking.id)
    const ids = unique([...linked.map(j => j.id), ...(booking.converted_job_id && jobById.has(booking.converted_job_id) ? [booking.converted_job_id] : [])])
    if (ids.length > 1 || (jobById.get(booking.converted_job_id)?.booking_request_id && jobById.get(booking.converted_job_id).booking_request_id !== booking.id)) ids.forEach(id => ambiguousJobs.add(id))
  }
  const rows = []
  for (const job of jobs) {
    const status = normaliseJobStatus(job.status)
    if (CANCELLED.has(status)) continue
    const p = planning(job, services, false)
    const member = team.find(t => t.id === job.technician_id && ['owner', 'technician'].includes(t.role))
    const scheduled = validDay(job.scheduled_date) ? job.scheduled_date : null
    const active = ACTIVE.has(status)
    const historical = HISTORICAL.has(status) && Boolean(scheduled && scheduled <= today)
    const excludedLoadReason = scheduled && (active || historical) && ambiguousJobs.has(job.id) ? 'Conversion authority unresolved'
      : scheduled && !active && !HISTORICAL.has(status) ? 'Status outside scheduling authority' : null
    const reasons = []
    if (!scheduled) reasons.push('Booked-in date missing')
    if (!member) reasons.push(job.technician_id ? 'Technician identity unresolved' : 'Technician unassigned')
    if (!p.complete || p.minutes === null) reasons.push('Service-book time incomplete')
    if (p.module === 'unknown') reasons.push('Module unresolved')
    const target = validDay(job.due_date) ? job.due_date : null
    const daysPast = active && target && target < today ? Math.round((Date.parse(`${today}T12:00Z`) - Date.parse(`${target}T12:00Z`)) / 86400000) : null
    rows.push({ entity_id: `job:${job.id}`, entity_type: 'job', id: job.id, job_number: job.job_number,
      customer_display_name: job.customer_display_name, service_summary: job.service_summary,
      source_url: `/jobs?job=${encodeURIComponent(job.id)}`,
      status, module_key: p.module, technician_id: member?.id || (job.technician_id ? 'unknown' : 'unassigned'),
      technician_name: member?.full_name || (job.technician_id ? 'Unknown technician' : 'Unassigned'),
      scheduled_date: scheduled, target_date: target, planned_minutes: p.minutes, planning_complete: p.complete && p.minutes !== null,
      planning_source: p.source, active, historical, contributes: Boolean(scheduled && (active || historical) && !ambiguousJobs.has(job.id)),
      excluded_load_reason: excludedLoadReason,
      provisional: false, scheduling_reasons: active ? reasons : [], waiting_reason: active ? WAITING[status] || null : null,
      days_past_target: daysPast, coverage_warnings: [...(ambiguousJobs.has(job.id) ? ['Ambiguous conversion: planned load excluded until links are checked'] : []), ...(p.module === 'unknown' ? ['Module unresolved'] : []), ...(!active && !HISTORICAL.has(status) ? ['Status outside scheduling rules'] : [])] })
  }
  for (const booking of bookings) {
    if (['cancelled', 'voided', 'declined'].includes(booking.status)) continue
    const linked = reverse.get(booking.id) || []
    const forward = booking.converted_job_id ? jobById.get(booking.converted_job_id) : null
    const linkedIds = unique([...linked.map(j => j.id), ...(forward ? [forward.id] : [])])
    const ambiguous = linkedIds.length > 1 || Boolean(booking.converted_job_id && !forward)
      || Boolean(forward?.booking_request_id && forward.booking_request_id !== booking.id)
    // Both link directions suppress provisional double counting. Ambiguities remain reviewable.
    if (!ambiguous && linkedIds.length === 1) continue
    const p = planning(booking, services, true)
    const scheduled = validDay(booking.preferred_date) ? booking.preferred_date : null
    const warnings = []
    if (ambiguous || booking.status === 'converted') warnings.push('Conversion link needs checking')
    if (p.module === 'unknown') warnings.push('Module unresolved')
    if (booking.status === 'accepted') warnings.push('Accepted booking without a linked job: excluded from provisional hours')
    if (!['pending', 'accepted', 'converted'].includes(booking.status)) warnings.push('Status outside scheduling rules')
    rows.push({ entity_id: `booking:${booking.id}`, entity_type: 'booking', id: booking.id, job_number: 'Booking',
      customer_display_name: booking.customer_display_name, service_summary: booking.service_summary,
      source_url: `/reporting/bookings/${encodeURIComponent(booking.id)}`, status: booking.status, module_key: p.module,
      technician_id: 'unassigned', technician_name: 'Unassigned', scheduled_date: scheduled, target_date: null,
      planned_minutes: p.minutes, planning_complete: p.complete && p.minutes !== null, planning_source: p.source,
      active: false, historical: false, contributes: Boolean(scheduled && booking.status === 'pending' && !ambiguous),
      excluded_load_reason: !scheduled ? null : ambiguous || booking.status === 'converted' ? 'Conversion authority unresolved'
        : booking.status !== 'pending' ? 'Pre-job status needs planning confirmation' : null,
      provisional: true, scheduling_reasons: [], waiting_reason: null, days_past_target: null, coverage_warnings: warnings })
  }
  return rows
}

function loadSummary(rows, capacity, excluded = []) {
  const confirmed = rows.filter(r => !r.provisional)
  const provisional = rows.filter(r => r.provisional)
  const sum = list => list.reduce((n, r) => n + (r.planned_minutes || 0), 0)
  const confirmedMinutes = sum(confirmed)
  const provisionalMinutes = sum(provisional)
  const incompleteTime = rows.filter(r => !r.planning_complete)
  const incomplete = incompleteTime.length + excluded.length
  return { confirmed_minutes: confirmedMinutes, provisional_minutes: provisionalMinutes,
    capacity_minutes: capacity, incomplete_count: incomplete,
    time_incomplete_count: incompleteTime.length, excluded_load_count: excluded.length,
    coverage_ids: [...incompleteTime, ...excluded].map(r => r.entity_id),
    excluded_load_ids: excluded.map(r => r.entity_id),
    coverage_reasons: unique([...incompleteTime.map(() => 'Service-book time incomplete'), ...excluded.map(r => r.excluded_load_reason)]),
    remaining_minutes: capacity === null || incomplete ? null : capacity - confirmedMinutes,
    over_minutes: capacity === null ? null : Math.max(0, confirmedMinutes - capacity),
    utilisation_percent: capacity === null || capacity === 0 || incomplete ? null : confirmedMinutes / capacity * 100,
    ids: rows.map(r => r.entity_id), confirmed_ids: confirmed.map(r => r.entity_id), provisional_ids: provisional.map(r => r.entity_id) }
}

export function buildCapacityReport(snapshot, { range = capacityPreset('week'), module = 'all', technician = 'all', search = '', today = dayAt() } = {}) {
  if (!validDay(range.from) || !validDay(range.to) || range.from > range.to || !validDay(today)
    || Date.parse(range.to) - Date.parse(range.from) > 366 * 86400000) throw new Error('Choose an inclusive period of no more than 366 days.')
  const allRows = capacityRows(snapshot, today)
  const q = search.trim().toLowerCase()
  const rows = allRows.filter(r => (module === 'all' || r.module_key === module)
    && (technician === 'all' || r.technician_id === technician)
    && (!q || [r.job_number, r.customer_display_name, r.service_summary].some(v => text(v).toLowerCase().includes(q))))
  const load = rows.filter(r => r.contributes && inRange(r.scheduled_date, range))
  // Excluded uncertain dated work is not zero demand. Carry its coverage through
  // the same filters/week/technician boundaries as the known contributing load.
  const excludedLoad = rows.filter(r => r.excluded_load_reason && inRange(r.scheduled_date, range))
  const weeks = []
  for (let start = monday(range.from); start <= range.to; start = addDays(start, 7)) {
    const end = addDays(start, 6)
    const partial = start < range.from || end > range.to
    const contributing = load.filter(r => monday(r.scheduled_date) === start)
    const configured = capacityMinutes(snapshot.workshop.weekly_bookable_hours)
    weeks.push({ key: start, label: `${start} – ${end}`, partial, configured_capacity_minutes: configured,
      ...loadSummary(contributing, partial ? null : configured, excludedLoad.filter(r => monday(r.scheduled_date) === start)) })
  }
  const wholeWeeks = weeks.every(w => !w.partial)
  const techGroups = [...snapshot.team.filter(t => ['owner', 'technician'].includes(t.role)).map(t => ({ id: t.id, label: t.full_name || 'Unnamed technician', capacity: capacityMinutes(t.weekly_bookable_hours) })),
    { id: 'unassigned', label: 'Unassigned', capacity: null }, { id: 'unknown', label: 'Unknown technician', capacity: null }]
    .filter(t => technician === 'all' || technician === t.id)
    .map(t => ({ key: t.id, label: t.label,
      ...loadSummary(load.filter(r => !r.provisional && r.technician_id === t.id), wholeWeeks && t.capacity !== null ? t.capacity * weeks.length : null,
        excludedLoad.filter(r => !r.provisional && r.technician_id === t.id)) }))
  return { rows, load, excluded_load: excludedLoad, weeks, technicians: techGroups,
    technician_weeks: weeks.flatMap(w => techGroups.map(t => ({ key: `${w.key}:${t.key}`, label: `${t.label} · ${w.key}`,
      ...loadSummary(load.filter(r => !r.provisional && r.technician_id === t.key && monday(r.scheduled_date) === w.key),
        w.partial ? null : capacityMinutes(snapshot.team.find(member => member.id === t.key)?.weekly_bookable_hours),
        excludedLoad.filter(r => !r.provisional && r.technician_id === t.key && monday(r.scheduled_date) === w.key)) }))),
    modules: ['general', 'suspension', 'unknown'].map(key => ({ key, label: key === 'unknown' ? 'Unresolved module' : key === 'general' ? 'General' : 'Suspension', ...loadSummary(load.filter(r => r.module_key === key), null, excludedLoad.filter(r => r.module_key === key)) })),
    needs_scheduling: rows.filter(r => r.scheduling_reasons.length),
    waiting: rows.filter(r => r.waiting_reason), past_target: rows.filter(r => r.days_past_target !== null),
    checks: rows.filter(r => r.coverage_warnings.length || (!r.planning_complete && r.contributes) || (r.provisional && !r.scheduled_date)),
    excluded_unknown_modules: module === 'all' ? 0 : allRows.filter(r => r.module_key === 'unknown').length,
    summary: loadSummary(load, wholeWeeks && weeks.every(w => w.capacity_minutes !== null) ? weeks.reduce((n, w) => n + w.capacity_minutes, 0) : null, excludedLoad) }
}
